import { useEffect, useRef, useState } from 'react';

interface IProps {
  src: string | null;
  // Some embeds (e.g. TikTok short links) don't carry a usable src upfront and need it
  // resolved asynchronously from the raw url.
  unresolvedUrl?: string;
  resolveSrc?: (url: string) => Promise<string | null>;
  title: string;
  height: number;
  // Scales the iframe with its container (e.g. 16:9 video) instead of using a fixed pixel height,
  // needed for embeds whose width should shrink on mobile.
  responsiveAspectRatio?: boolean;
  maxWidthClassName?: string;
  className?: string;
  autoResize?: boolean;
}

export const SocialEmbedIframe = ({
  src,
  unresolvedUrl,
  resolveSrc,
  title,
  height,
  responsiveAspectRatio,
  maxWidthClassName,
  className,
  autoResize,
}: IProps) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [resizedHeight, setResizedHeight] = useState<number | null>(null);
  const [resolvedSrc, setResolvedSrc] = useState(src);

  useEffect(() => {
    if (resolvedSrc || !resolveSrc || !unresolvedUrl) return;

    const resolve = async () => {
      const result = await resolveSrc(unresolvedUrl);
      if (result) setResolvedSrc(result);
    };
    resolve();
  }, [resolvedSrc, resolveSrc, unresolvedUrl]);

  useEffect(() => {
    if (!autoResize) return;

    const handleMessage = (event: MessageEvent) => {
      if (event.source !== iframeRef.current?.contentWindow) return;

      try {
        const data = JSON.parse(event.data);
        if (data?.type === 'MEASURE' && data?.details?.height) {
          setResizedHeight(data.details.height);
        }
      } catch {
        // ignore invalid messages
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [autoResize]);

  if (!resolvedSrc) return null;

  const iframeSharedProps = {
    ref: iframeRef,
    src: resolvedSrc,
    title,
    loading: 'lazy' as const,
    scrolling: 'no' as const,
    sandbox: 'allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox',
    referrerPolicy: 'strict-origin-when-cross-origin' as const,
    allow: 'encrypted-media; picture-in-picture',
  };

  if (responsiveAspectRatio) {
    return (
      <div
        className={`relative mx-auto my-4 aspect-video w-full overflow-hidden ${maxWidthClassName ?? ''} ${className ?? ''}`}>
        <iframe
          {...iframeSharedProps}
          width="100%"
          height="100%"
          className="absolute inset-0 h-full w-full border-0"
        />
      </div>
    );
  }

  return (
    <div className={`mx-auto my-4 w-full ${maxWidthClassName ?? ''} ${className ?? ''}`}>
      <iframe
        {...iframeSharedProps}
        width="100%"
        height={resizedHeight ?? height}
        className="overflow-hidden border-0"
      />
    </div>
  );
};
